import { Call, Device, TwilioError } from '@twilio/voice-sdk';
import { useRef, useState } from 'react';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ApiClient } from './api';
import { isLiveCall } from './callActivity';
import { CallEndCause, CallEndReporter, EndReason } from './callEndReport';
import { claimTwilioDevice, releaseTwilioDevice } from './twilioDeviceLock';

interface IProps {
	onError: (twilioError: TwilioError.TwilioError) => void;
	onIncomingCall: (phoneCallId: string) => void;
}

export const useTwilioDialer = ({ onError, onIncomingCall }: IProps) => {
	const [device, setDevice] = useState<Device>(undefined);
	const [call, setCall] = useState<Call>(undefined);
	const [isConnected, setIsConnected] = useState<boolean>(false);
	const [isRinging, setIsRinging] = useState<boolean>(false);
	const [isMuted, updateIsMuted] = useState<boolean>(false);
	const [deviceState, setDeviceState] = useState<Device.State>(undefined);
	// Mirror of `device` for the Twilio device-lock evictor — the evict
	// callback registered with `claimTwilioDevice` cannot read the latest
	// React state directly, so we proxy through a ref kept in sync below.
	const deviceRef = useRef<Device | undefined>(undefined);
	// Refs, not state: the Device and Call listeners are registered once inside
	// `init()` and would otherwise read the values captured by the render that ran it.
	const reporterRef = useRef<CallEndReporter | undefined>(undefined);
	const callRef = useRef<Call | undefined>(undefined);

	const session = useUserSession();

	const getReporter = () => {
		if (!reporterRef.current) {
			reporterRef.current = new CallEndReporter(new ApiClient(session));
		}
		return reporterRef.current;
	};

	const reportEnd = (endedCall: Call | undefined, cause: CallEndCause, reason: EndReason, errorCode?: number) => {
		if (!endedCall) return;
		getReporter().report(endedCall, cause, reason, { deviceState: deviceRef.current?.state, errorCode });
	};

	const hasLiveCall = () => isLiveCall(callRef.current);

	const destroyDevice = (target: Device) => {
		try {
			target.disconnectAll();
			target.destroy();
		} catch (ex) {
			console.error('failed to destroy device', ex);
		}
	};

	const createDevice = (token: string) => {
		const newDevice = new Device(token, {
			allowIncomingWhileBusy: false,
			closeProtection: 'You are on a call. Closing this tab will end it.',
		});

		newDevice.on(Device.EventName.Registering, () => {
			console.log('device registering', newDevice.identity);
			setDeviceState(Device.State.Registering);
		});

		newDevice.on(Device.EventName.Registered, () => {
			console.log('device registered', newDevice.identity);
			console.log('device info:', newDevice.edge, newDevice.home, newDevice.state);
			setDeviceState(Device.State.Registered);
		});

		newDevice.on(Device.EventName.Unregistered, () => {
			console.log('device unregistered', newDevice.identity);
			setDeviceState(Device.State.Unregistered);
		});

		return newDevice;
	};

	const addCallListeners = (newCall: Call, direction: 'inbound' | 'outbound') => {
		setCall(newCall);
		callRef.current = newCall;
		getReporter().trackCall(newCall, direction);
		updateIsMuted(newCall.isMuted());

		newCall.on('accept', callArg => {
			console.log(
				'The incoming call was accepted or the outgoing call media session has finished setting up.',
				callArg
			);
			getReporter().markLive(newCall);
			setIsConnected(true);
			setIsRinging(false);
		});

		newCall.on('reject', () => {
			console.log('The call was rejected.');
			if (callRef.current === newCall) callRef.current = undefined;
			setIsConnected(false);
			setIsRinging(false);
		});

		newCall.on('disconnect', callArg => {
			console.log('The call has been disconnected.', callArg);

			// A superseded Call still emits, and must not consume the cause stamped for the
			// live call nor clear the ref the device-level teardown paths report against.
			const isCurrent = callRef.current === newCall;
			// No stamped cause means nothing here asked for it — the far end hung up.
			reportEnd(newCall, isCurrent ? getReporter().takeCause('remote-hangup') : 'remote-hangup', 'disconnect');
			if (isCurrent) callRef.current = undefined;
			setCall(undefined);
			setIsConnected(false);
			setIsRinging(false);
		});

		newCall.on('cancel', () => {
			console.log('The call has been canceled.');

			reportEnd(newCall, 'call-cancel', 'cancel');
			if (callRef.current === newCall) callRef.current = undefined;
			setCall(undefined);
			setIsConnected(false);
			setIsRinging(false);
		});

		newCall.on('error', (twilioError: TwilioError.TwilioError) => {
			reportEnd(newCall, 'call-error', 'error', twilioError?.code);
			onError(twilioError);
		});

		newCall.on('mute', value => {
			updateIsMuted(value);
		});

		newCall.on('reconnected', () => {
			console.log('The call has regained connectivity.');
		});

		newCall.on('reconnecting', (twilioError: TwilioError.TwilioError) => {
			// update the UI to alert user that connectivity was lost
			// and that the SDK is trying to reconnect
			// showReconnectingMessageInBrowser();

			// You may also want to view the TwilioError:
			console.log('Connectivity error: ', twilioError);
		});
	};

	const init = async () => {
		// Claim the process-wide Twilio Device lock before constructing a
		// Device. If the focused-view pipeline owns it (e.g. mid-route-
		// transition between `/focused-view` and `/queue`), the evictor
		// fires on its controller so we don't run two Devices on the same
		// identity. The evictor reads the latest device through `deviceRef`
		// so it always evicts the most recent instance.
		claimTwilioDevice('legacy-telephony-context', () => {
			const current = deviceRef.current;
			if (!current) return;
			reportEnd(callRef.current, 'device-evicted', 'hangup');
			callRef.current = undefined;
			destroyDevice(current);
			deviceRef.current = undefined;
			setDevice(undefined);
		});

		const client = new ApiClient(session);
		const result = await client.getTwilioAccessToken();
		if (!result.success) {
			// Never drop the ref to a Device we still hold: it stays registered with Twilio and
			// becomes unreachable. Keep it if the rep is mid-call, otherwise tear it down.
			const stranded = deviceRef.current;
			if (stranded && hasLiveCall()) return stranded;
			if (stranded) destroyDevice(stranded);
			setDevice(undefined);
			deviceRef.current = undefined;
			releaseTwilioDevice('legacy-telephony-context');
			return undefined;
		}

		const accessToken = result.value;

		// An existing Device stays registered and keeps taking inbound legs, so 31202 re-init
		// must replace it. Placed after the token fetch so a failed fetch leaves it alone.
		const existing = deviceRef.current;
		if (existing) {
			if (hasLiveCall()) {
				// Rebuilding would disconnect the leg the rep is on; refresh in place.
				existing.updateToken(accessToken);
				return existing;
			}
			destroyDevice(existing);
			deviceRef.current = undefined;
			setDevice(undefined);
		}

		const newDevice = createDevice(accessToken);

		newDevice.on(Device.EventName.TokenWillExpire, async () => {
			console.log('token will expire');
			const tokenResult = await client.getTwilioAccessToken();
			if (tokenResult.success) {
				newDevice.updateToken(tokenResult.value);
			}
		});

		newDevice.on(Device.EventName.Incoming, (incomingCall: Call) => {
			console.log('incoming call', incomingCall);

			// `allowIncomingWhileBusy` is per-Device, so a leg rung at another registration of
			// the same identity still lands here. Before `addCallListeners`, which would adopt it.
			if (hasLiveCall()) {
				console.warn('ignoring an incoming call while already on one', incomingCall.parameters?.CallSid);
				incomingCall.ignore();
				return;
			}

			addCallListeners(incomingCall, 'inbound');

			if (incomingCall.customParameters.get('autoAnswer') === 'True') {
				incomingCall.accept();
			} else {
				setIsRinging(true);
				const phoneCallId = incomingCall.customParameters.get('phoneCallId');
				if (onIncomingCall) {
					onIncomingCall(phoneCallId);
				}
			}
		});

		newDevice.on(Device.EventName.Error, (twilioError: TwilioError.TwilioError, callArg) => {
			console.log('An error has occurred: ', twilioError, callArg);
			switch (twilioError.code) {
				case 31002:
					// call disconnected
					console.log('call got disconnected');
					// Report before hanging up, and stamp so the ensuing teardown is not
					// recorded as the rep doing it.
					getReporter().stampCause('device-error');
					reportEnd(callRef.current, 'device-error', 'hangup');
					callRef.current = undefined;
					hangUp();
					return;

				case 31202:
					// token has expired, auto reconnect
					// Not sure if we need this, I left this here because previously we needed to.
					// Now, the SDK emits tokenWillExpire event and we can update the token there.
					init();
					return;

				default:
					onError(twilioError);
					break;
			}
		});

		newDevice.on('connect', (connection: Call) => {
			console.log('Call is connected', connection);
			if (call !== connection) {
				setCall(connection);
			}
		});

		newDevice.on('disconnect', (connection: Call) => {
			console.log('Call was disconnected', connection);
			if (call === connection) {
				setCall(undefined);
			}
		});

		newDevice.on('offline', () => {
			console.warn('Device is offline');
			deInit('device-offline');
		});

		newDevice.on('ready', () => {
			console.log('Device is ready');
		});

		// Previously, there was a setup method that would implicitly register the device for inbound calls.
		// With 2.x we need to explicitly call register.
		newDevice.register();

		setDevice(newDevice);
		deviceRef.current = newDevice;

		return newDevice;
	};

	const deInit = (cause: CallEndCause = 'legacy-teardown') => {
		// Report before destroying the Device: `destroy()` tears the listeners down, so
		// the `disconnect` it provokes is not guaranteed to survive to carry the cause.
		if (callRef.current) {
			getReporter().stampCause(cause);
			reportEnd(callRef.current, cause, 'hangup');
			callRef.current = undefined;
		}

		// The ref, not the state: the `offline` listener calls this through the closure `init()`
		// ran in, where `device` was still unset, leaving the Device alive and registered.
		const current = deviceRef.current;
		if (current) {
			destroyDevice(current);
			setDevice(undefined);
			deviceRef.current = undefined;
		}
		releaseTwilioDevice('legacy-telephony-context');
	};

	const dial = async (phoneNumber: string, parameters?: Record<string, string>) => {
		let currentDevice = deviceRef.current;
		if (!currentDevice) {
			// will try to initiate implicitly but ...
			// from testing it seems that we would still need to add some delay before trying to make a call or it will fail
			currentDevice = await init();
		}

		if (!currentDevice) {
			console.error('failed to initiate twilio device');
			return false;
		}

		const newCall = await currentDevice.connect({
			params: {
				...parameters,
				To: phoneNumber,
			},
		});

		addCallListeners(newCall, 'outbound');

		return true;
	};

	const hangUp = () => {
		setIsConnected(false);
		// Keep a more specific cause if one was already stamped — the Device error path
		// hangs up through here and must not be recorded as the rep doing it.
		getReporter().stampCauseIfUnset('user-hangup');

		// Ref, not state — the 31002 handler hangs up through the closure `init()` ran in.
		deviceRef.current?.disconnectAll();
	};

	const acceptCall = () => {
		if (call && call.status() === Call.State.Pending) {
			call.accept();
			return true;
		}

		return false;
	};

	const rejectCall = () => {
		if (call && call.status() === Call.State.Pending) {
			call.ignore();
			return true;
		}

		return false;
	};

	const setIsMuted = (value: boolean) => {
		if (call) {
			call.mute(value);
		}
	};

	const sendDigits = (digit: string) => {
		if (!!call && call.status() === Call.State.Open) {
			call.sendDigits(digit);
		}
	};

	return {
		acceptCall,
		call,
		deInit,
		device,
		deviceState,
		dial,
		hangUp,
		init,
		isConnected,
		isMuted,
		isRinging,
		rejectCall,
		sendDigits,
		setIsMuted,
	};
};
